/**
 * The platform's two marks and its name, as every screen draws them.
 *
 *   agency   the developer's mark — top of every dashboard, above the
 *            sign-in card, the landing page's credits
 *   product  the platform's mark — the sidebar, the sign-in card, the
 *            landing page
 *
 * The Super Admin uploads either from «هوية المنصة» (Backend/lib/branding.js);
 * a slot with nothing uploaded draws the built-in mark, so an installation
 * that never opens that screen looks exactly as it always has.
 *
 * An uploaded mark is drawn whole, at its own proportions, on the plate the
 * Super Admin picked (domain/logoBackdrop.js) — a wide wordmark is never
 * squeezed into the square the built-in mark sits in, and a logo in dark
 * lines keeps its white plate on the dark themes.
 */

export const BUILT_IN = Object.freeze({
  name: 'كبسولة',
  agency: Object.freeze({ src: '/abbas-jr-developments.png', alt: 'ABBAS JR Developments', backdrop: 'none', custom: false }),
  product: Object.freeze({ src: '/capsule-logo.png', alt: 'كبسولة', backdrop: 'none', custom: false }),
});

export const BRAND_SLOTS = Object.freeze(['agency', 'product']);

/**
 * The server's summary (GET /api/branding) as the screens use it.
 *
 * `name` is the platform's name as the screens print it: the one the Super
 * Admin set, else «كبسولة» — unless a mark was uploaded without a name, when
 * it is empty and the screens say «مرحباً بك» rather than name the old one
 * beside the new mark.
 *
 * @param {object|null} summary  `{ name, agency, product }`, each slot null or `{ hash, backdrop }`
 * @param {string} apiBase       where the API is served ("/api")
 */
export function resolveBranding(summary, apiBase = '/api') {
  const named = (summary && typeof summary.name === 'string' && summary.name.trim()) || '';
  const out = { custom: false };
  for (const slot of BRAND_SLOTS) {
    const entry = summary && summary[slot];
    if (entry && /^[0-9a-f]{8,64}$/.test(String(entry.hash || ''))) {
      out[slot] = {
        src: `${apiBase}/branding/logo/${slot}?v=${entry.hash}`,
        alt: slot === 'product' ? named || 'شعار المنصة' : 'شعار جهة التطوير',
        backdrop: ['none', 'light', 'dark'].includes(entry.backdrop) ? entry.backdrop : 'light',
        custom: true,
      };
      out.custom = true;
    } else {
      out[slot] = { ...BUILT_IN[slot], ...(slot === 'product' && named ? { alt: named } : {}) };
    }
  }
  out.named = Boolean(named);
  out.name = named || (out.custom ? '' : BUILT_IN.name);
  return out;
}

/** «مرحباً بك في كبسولة», or without a name when the marks are new and nameless. */
export const welcomeLine = (brand) => (brand.name ? `مرحباً بك في ${brand.name}` : 'مرحباً بك');

/** The name in a public page's browser tab: «Capsule» as before, or the Super Admin's. */
export const titleName = (brand) => (brand.named ? brand.name : brand.custom ? '' : 'Capsule');

/** The help assistant's title. */
export const assistantTitle = (brand) => (brand.name ? `مساعد ${brand.name}` : 'المساعد الذكي');

/**
 * The line printed under a prescription or a medical record: the built-in
 * one, the Super Admin's name, or nothing for new marks without a name.
 */
export function poweredByLine(brand) {
  if (!brand || (!brand.custom && !brand.named)) return 'Powered by Capsule Medical System';
  return brand.named ? `Powered by ${brand.name}` : '';
}
